import type { SsrAppApiDirectOptions } from '@/data/appApi';
import apiRequest from '@/utils/apiRequest';
import { isServerMaintenanceResponse } from '@/utils/apiRequest/isServerMaintenanceResponse';
import { RequestUserData } from '@/utils/apiRequest/types';
import { calmLogger } from '@/utils/calmLogger';

export interface DeviceConsent {
	ip_country: string | undefined | null;
	requires_gdpr: boolean;
	requires_gdpr_choice: boolean;
	was_server_maintenance_error?: boolean;
}

/**
 * This request is always made as part of SSR
 * so it's been selected as the one that should check for
 * our API servers undergoing maintenance during SSR.
 * If this is no longer a request guaranteed to happen during SSR,
 * that check should move elsewhere
 */
export const getDeviceConsentAndCheckForServerMaintenance = async (
	hasAcceptGdprCookiesKey: string | undefined,
	userData: RequestUserData,
	opts?: SsrAppApiDirectOptions,
): Promise<DeviceConsent> => {
	try {
		// `<DeviceConsent>` only asserts the expected shape, not the runtime one — a 2xx
		// with an empty body deserializes to a falsy value here.
		let data: DeviceConsent | undefined;
		let status: number | undefined;
		let raw: unknown;
		if (typeof window === 'undefined' && opts?.ctx) {
			// SSR app-api-direct: see getFeatureFlags.ts. `requiresAuth: true`
			// matches the authproxy (Bearer injected for /device/consent).
			const { appApiServerRequest } = await import('@/data/appApi');
			const result = await appApiServerRequest<DeviceConsent>({
				endpoint: 'device/consent',
				ctx: opts.ctx,
				xLanguage: opts.xLanguage,
				requiresAuth: true,
			});
			data = result.data;
			status = result.status;
			raw = result;
		} else {
			const result = await apiRequest<DeviceConsent>({
				endpoint: 'device/consent',
				method: 'GET',
				userData,
			});
			data = result.data;
			status = result.status;
			raw = result;
		}

		if (!data) {
			const isSuccessStatus = status !== undefined && status >= 200 && status < 300;
			if (!isSuccessStatus) {
				// The shared axios interceptor (apiRequest/index.ts) resolves rather than
				// rejects some failures — e.g. a total network error, or a redirect status —
				// with a `data` just as falsy as a genuine empty body; the naive `!data` check
				// alone would misclassify them as one.
				const { message, code } = raw as { message?: string; code?: string };
				throw new Error(
					`GET /device/consent resolved without a 2xx response or body: ${
						message ?? code ?? `resolved with status ${status}`
					}`,
					{ cause: raw },
				);
			}

			// Genuine 2xx with an empty body degrades intentionally here, not via the catch.
			calmLogger.warn('GET /device/consent returned a 2xx with an empty body', {
				source: typeof window === 'undefined' && opts?.ctx ? 'app-api-direct' : 'authproxy',
			});

			return {
				ip_country: null,
				requires_gdpr: false,
				requires_gdpr_choice: false,
				was_server_maintenance_error: false,
			};
		}

		return {
			...data,
			requires_gdpr_choice: data.requires_gdpr,
			requires_gdpr: hasAcceptGdprCookiesKey !== undefined ? !hasAcceptGdprCookiesKey : data.requires_gdpr,
		};
	} catch (err) {
		calmLogger.error('Error retrieving device consent via GET /device/consent', {}, err);

		return {
			ip_country: null,
			requires_gdpr: false,
			requires_gdpr_choice: false,
			was_server_maintenance_error: isServerMaintenanceResponse(err),
		};
	}
};
